// src/components/viewer/ApprovalStatusBanner.jsx

import React, { useState, useEffect } from 'react';

const STATUS_CONFIG = {
  APPROVED: {
    icon: '✅',
    label: 'APPROVED',
    color: '#0d6832',
    bgColor: '#d4edda',
    borderColor: '#28a745',
    message: 'This request has been approved.',
    showConfetti: true,
  },
  DENIED: {
    icon: '❌',
    label: 'DENIED',
    color: '#721c24',
    bgColor: '#f8d7da',
    borderColor: '#dc3545',
    message: 'This request has been denied.',
    showConfetti: false,
  },
  PENDING_APPROVAL: {
    icon: '⏳',
    label: 'PENDING APPROVAL',
    color: '#856404',
    bgColor: '#fff3cd',
    borderColor: '#ffc107',
    message: 'Waiting for approver action.',
    showConfetti: false,
  },
  CANCELLED: {
    icon: '🚫',
    label: 'CANCELLED',
    color: '#383d41',
    bgColor: '#e2e3e5',
    borderColor: '#6c757d',
    message: 'This request has been cancelled.',
    showConfetti: false,
  },
  EXPIRED: {
    icon: '⏰',
    label: 'EXPIRED',
    color: '#383d41',
    bgColor: '#e2e3e5',
    borderColor: '#6c757d',
    message: 'This approval request has expired.',
    showConfetti: false,
  },
  ON_HOLD: {
    icon: '⏸️',
    label: 'ON HOLD',
    color: '#004085',
    bgColor: '#cce5ff',
    borderColor: '#007bff',
    message: 'This request is on hold.',
    showConfetti: false,
  },
  APPROVAL_FAILED: {
    icon: '⚠️',
    label: 'SUBMISSION FAILED',
    color: '#721c24',
    bgColor: '#f8d7da',
    borderColor: '#dc3545',
    message: 'Failed to create approval. Please resubmit.',
    showConfetti: false,
  },
};

const ApprovalStatusBanner = ({ data, isRefreshing }) => {
  const [timeAgo, setTimeAgo] = useState('');

  const approvalStatus = data?.approvalStatus;
  const approvalId = data?.approvalId;
  // Always construct the correct prod URL from approvalId, ignore stored approvalsUrl
  const approvalsUrl = approvalId ? `https://approvals.amazon.com/approval/${approvalId}/details/memo-view/notes-and-summary` : null;
  const lastApprover = data?.lastApproverAlias;
  const approvalCompletedAt = data?.approvalCompletedAt;
  const approvalLastUpdated = data?.approvalLastUpdated;
  const approvalCreatedAt = data?.approvalCreatedAt;
  const l8Manager = data?.l8ManagerAlias;
  const requester = data?.requesterAlias;
  const approvalError = data?.approvalError;
  const provisioningStatus = data?.provisioningStatus;

  // Calculate time ago
  useEffect(() => {
    const calculateTimeAgo = () => {
      const refTime = approvalCompletedAt || approvalLastUpdated || approvalCreatedAt;
      if (!refTime) return;

      const diff = Date.now() - new Date(refTime).getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) setTimeAgo(`${days}d ago`);
      else if (hours > 0) setTimeAgo(`${hours}h ago`);
      else if (minutes > 0) setTimeAgo(`${minutes}m ago`);
      else setTimeAgo('just now');
    };

    calculateTimeAgo();
    const interval = setInterval(calculateTimeAgo, 60000);
    return () => clearInterval(interval);
  }, [approvalCompletedAt, approvalLastUpdated, approvalCreatedAt]);

  // Don't render if no approval status
  if (!approvalStatus) return null;

  const config = STATUS_CONFIG[approvalStatus] || {
    icon: '❓',
    label: approvalStatus,
    color: '#383d41',
    bgColor: '#e2e3e5',
    borderColor: '#6c757d',
    message: `Status: ${approvalStatus}`,
    showConfetti: false,
  };

  const isTerminal = ['APPROVED', 'DENIED', 'CANCELLED', 'EXPIRED'].includes(approvalStatus);
  const isPending = approvalStatus === 'PENDING_APPROVAL';

  // Determine if this is a 3-stage HRP/CRP request
  const riskTypeGroup = (data?.riskTypeGroup || '').toUpperCase();
  const isHighRisk = riskTypeGroup === 'HRP' || riskTypeGroup === 'CRP';
  const isUnlisted = (data?.requestType || '').includes('unlisted');
  const leaderLevel = riskTypeGroup === 'CRP' ? 'L8' : riskTypeGroup === 'HRP' ? 'L7' : 'L6';

  return (
    <div
      className="approval-status-banner"
      style={{
        background: config.bgColor,
        border: `2px solid ${config.borderColor}`,
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Refresh indicator */}
      {isRefreshing && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, transparent, ${config.borderColor}, transparent)`,
            animation: 'shimmer 1.5s infinite',
          }}
        />
      )}

      {/* Main status row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* Left: Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '32px' }}>{config.icon}</span>
          <div>
            <div
              style={{
                fontSize: '20px',
                fontWeight: '700',
                color: config.color,
                letterSpacing: '0.5px',
              }}
            >
              {config.label}
            </div>
            <div style={{ fontSize: '14px', color: config.color, opacity: 0.85, marginTop: '2px' }}>
              {config.message}
              {timeAgo && <span style={{ marginLeft: '8px', opacity: 0.7 }}>• {timeAgo}</span>}
            </div>
          </div>
        </div>

        {/* Right: Link to approvals.amazon.com */}
        {approvalsUrl && (
          <a
            href={approvalsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: config.color,
              color: '#fff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '13px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              transition: 'opacity 0.2s',
            }}
            onMouseOver={(e) => (e.target.style.opacity = '0.85')}
            onMouseOut={(e) => (e.target.style.opacity = '1')}
          >
            🔗 View on Approvals
          </a>
        )}
      </div>

      {/* Part A: "Currently awaiting" one-liner — at-a-glance "what's it waiting on".
          Derived from the SAME cached fields the stage timeline uses (no live call). */}
      {isUnlisted && isPending && (() => {
        const s1Done = !!(data?.stage1ApprovedBy);
        const s2Done = data?.l8ApprovalStatus === 'APPROVED';
        let awaiting;
        if (isHighRisk) {
          // 3-stage: SPS → Leadership → Access Management
          awaiting = !s1Done ? 'SPS Permissions Team'
            : !s2Done ? `${leaderLevel} Leadership approval`
            : 'Access Management Team';
        } else {
          // 2-stage: Leadership → Permissions Team
          awaiting = !s2Done ? `${leaderLevel} Leadership approval` : 'Permissions Team';
        }
        return (
          <div style={{
            marginTop: '12px',
            fontSize: '13px',
            color: config.color,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <span style={{ opacity: 0.7 }}>⏳ Currently awaiting:</span>
            <span style={{ fontWeight: '700' }}>{awaiting}</span>
          </div>
        );
      })()}

      {/* 3-Stage Approval Progress for HRP/CRP Unlisted */}
      {isUnlisted && isHighRisk && isPending && (
        <div style={{
          marginTop: '16px',
          paddingTop: '14px',
          borderTop: `1px solid ${config.borderColor}40`,
        }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: config.color, marginBottom: '12px' }}>
            📋 Approval Stages ({riskTypeGroup})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(() => {
              const s1Done = !!(data?.stage1ApprovedBy);
              const s2Done = data?.l8ApprovalStatus === 'APPROVED';
              const s1Status = s1Done ? 'completed' : 'active';
              const s2Status = !s1Done ? 'waiting' : (s2Done ? 'completed' : 'active');
              const s3Status = !s2Done ? 'waiting' : 'active';
              return (
                <>
                  <ApprovalStageRow
                    stageNum={1}
                    label="SPS Permissions Team"
                    sublabel={s1Done ? `Approved by ${data.stage1ApprovedBy}` : 'Awaiting Security Team Approval'}
                    status={s1Status}
                  />
                  <ApprovalStageRow
                    stageNum={2}
                    label={`${leaderLevel} Approval`}
                    sublabel={s2Done ? `Approved by ${data.l8ApproverAlias || l8Manager}` : 'Awaiting Leadership Approval'}
                    status={s2Status}
                  />
                  <ApprovalStageRow
                    stageNum={3}
                    label="Access Management Team"
                    sublabel="Awaiting Permission Team Approval"
                    status={s3Status}
                  />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* 2-Stage display for LRP/MRP Unlisted */}
      {isUnlisted && !isHighRisk && isPending && (
        <div style={{
          marginTop: '16px',
          paddingTop: '14px',
          borderTop: `1px solid ${config.borderColor}40`,
        }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: config.color, marginBottom: '12px' }}>
            📋 Approval Stages ({riskTypeGroup || 'LRP/MRP'})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(() => {
              const leaderDone = data?.l8ApprovalStatus === 'APPROVED';
              const s1Status = leaderDone ? 'completed' : 'active';
              const s2Status = leaderDone ? 'active' : 'waiting';
              return (
                <>
                  <ApprovalStageRow
                    stageNum={1}
                    label={`${leaderLevel} Manager Approval`}
                    sublabel={leaderDone ? `Approved by ${data.l8ApproverAlias || l8Manager}` : 'Awaiting Leadership Approval'}
                    status={s1Status}
                  />
                  <ApprovalStageRow
                    stageNum={2}
                    label="CTPS-Permissions-Security"
                    sublabel="Awaiting Security Team Review"
                    status={s2Status}
                  />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Details row */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '14px',
          paddingTop: '14px',
          borderTop: `1px solid ${config.borderColor}40`,
        }}
      >
        {approvalId && (
          <DetailChip label="Approval ID" value={approvalId} color={config.color} />
        )}
        {requester && (
          <DetailChip label="Requester" value={requester} color={config.color} />
        )}
        {l8Manager && (
          <DetailChip label="L8 Manager" value={l8Manager} color={config.color} />
        )}
        {lastApprover && isTerminal && (
          <DetailChip label="Last Action By" value={lastApprover} color={config.color} />
        )}
        {approvalCompletedAt && (
          <DetailChip
            label="Completed"
            value={new Date(approvalCompletedAt).toLocaleString()}
            color={config.color}
          />
        )}
        {provisioningStatus === 'READY_TO_PROVISION' && approvalStatus === 'APPROVED' && (
          <DetailChip label="Provisioning" value="Ready ✅" color={config.color} />
        )}
      </div>

      {/* Error details for failed submissions */}
      {approvalError && (
        <div
          style={{
            marginTop: '12px',
            padding: '10px 14px',
            background: 'rgba(0,0,0,0.05)',
            borderRadius: '8px',
            fontSize: '13px',
            color: config.color,
          }}
        >
          <strong>Error:</strong> {approvalError}
        </div>
      )}

      {/* Auto-refresh indicator for pending */}
      {isPending && (
        <div
          style={{
            marginTop: '12px',
            fontSize: '12px',
            color: config.color,
            opacity: 0.7,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span className="pulse-dot" style={{ background: config.borderColor }} />
          Auto-refreshing every 30 seconds...
        </div>
      )}
    </div>
  );
};

const DetailChip = ({ label, value, color }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: color,
    }}
  >
    <span style={{ opacity: 0.7 }}>{label}:</span>
    <span style={{ fontWeight: '600' }}>{value}</span>
  </div>
);

const ApprovalStageRow = ({ stageNum, label, sublabel, status }) => {
  // status: 'completed' | 'active' | 'waiting'
  const styles = {
    completed: { icon: '✅', bg: '#dcfce7', border: '#bbf7d0', color: '#166534' },
    active: { icon: '🔄', bg: '#fff3cd', border: '#fde68a', color: '#92400e' },
    waiting: { icon: '⏸️', bg: '#f3f4f6', border: '#e5e7eb', color: '#6b7280' },
  };
  const s = styles[status] || styles.waiting;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 14px',
      borderRadius: '8px',
      background: s.bg,
      border: `1px solid ${s.border}`,
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: status === 'active' ? '#fbbf24' : status === 'completed' ? '#22c55e' : '#d1d5db',
        color: '#fff', fontSize: '13px', fontWeight: '700', flexShrink: 0,
      }}>
        {stageNum}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: s.color }}>{label}</div>
        <div style={{ fontSize: '12px', color: s.color, opacity: 0.8, marginTop: '1px' }}>{sublabel}</div>
      </div>
      <span style={{ fontSize: '16px' }}>{s.icon}</span>
    </div>
  );
};

export default ApprovalStatusBanner;